/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { api } from '@/lib/api';
import { taskKeys } from '@/queries/tasks';
import { AUTO_REFRESH_MS } from '@/lib/queryClient';
import { Tip } from './Tip';
import { cn } from '@/lib/cn';
import { InlineNote } from '@/task/RichText';

/**
 * The notifications button's icon. Drawn in `currentColor` rather than the
 * `white` of the source artwork, so it takes the button's own text colour and
 * stays visible in both themes and on hover.
 */
function NotificationIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 17"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8.182 11.796L9.094 13.566C9.472 14.303 9.18 15.207 8.443 15.585C7.706 15.963 6.802 15.671 6.424 14.934L3.75 9.75" />
      <path d="M8.75 3.75H3.75C2.093 3.75 0.75 5.093 0.75 6.75C0.75 8.407 2.093 9.75 3.75 9.75H8.75C11.75 9.75 14.75 12.75 14.75 12.75V0.75C14.75 0.75 11.75 3.75 8.75 3.75Z" />
    </svg>
  );
}

/**
 * What happened while you were not looking.
 *
 * **No UI test holds this screen** — which is exactly why it is worth saying that
 * it is here. The selector contract can only protect what a test already asks
 * for, so a feature nobody wrote a test for is invisible to it, and this is the
 * category of thing the migration dropped twice already. It is in the plan's P4
 * list; that list is the check here, not the suite.
 *
 * The server scopes the feed to circles you can currently see, so somebody
 * removed from a circle stops getting its titles rather than keeping a feed of
 * links they can no longer open. Nothing here re-filters it.
 */
const Notification = z.object({
  id: z.union([z.string(), z.number()]),
  task_id: z.string(),
  type: z.string(),
  title: z.string().nullable().optional(),
  actor_name: z.string().nullable().optional(),
  from: z.string().nullable().optional(),
  to: z.string().nullable().optional(),
  vertical: z.string().nullable().optional(),
  excerpt: z.string().nullable().optional(),
  created_at: z.number(),
  isRead: z.boolean(),
});
const NotificationsResponse = z.object({
  items: z.array(Notification),
  unreadCount: z.number(),
});
type Notification = z.infer<typeof Notification>;

const VERB: Record<string, string> = {
  created: 'created',
  moved: 'moved',
  message: 'commented on',
  assigned: 'assigned you to',
  updated: 'updated',
};

export function Notifications() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => NotificationsResponse.parse(await api.get('/api/notifications')),
    refetchInterval: AUTO_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const markRead = useMutation({
    mutationFn: () => api.post('/api/notifications/read', {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['notifications'] });
      // The unseen dot on each card is the same fact seen from the board's side,
      // so it has to be re-asked or a read feed would sit above a dotted board.
      await qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });

  const items = data?.items ?? [];
  const unread = data?.unreadCount ?? 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      {/* The `title` this button used to carry is gone: `Tip` and a native
          tooltip would both fire, one over the other, half a second apart. The
          accessible name stays on the button and still counts the unread — the
          tooltip is the sighted person's label, not the screen reader's. */}
      <Tip label="Notifications">
        <Popover.Trigger asChild>
          <button
            id="notif-btn"
            aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
            className="tap relative flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink-muted transition-colors duration-fast ease-out-soft hover:border-border-strong hover:text-ink"
          >
            <NotificationIcon className="size-[17px]" />
            {/* Capped at 9+. An exact count past that is a number nobody acts on
                differently, and it stops the badge growing wider than the button. */}
            <span
              id="notif-badge"
              style={{ display: unread ? 'flex' : 'none' }}
              className="absolute -right-1 -top-1 size-[17px] items-center justify-center rounded-full bg-danger text-2xs font-bold text-bg"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          </button>
        </Popover.Trigger>
      </Tip>

      <Popover.Portal>
        <Popover.Content
          id="notif-panel"
          align="end"
          sideOffset={8}
          className="motion-surface open z-50 flex max-h-[70vh] w-[min(360px,92vw)] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-overlay"
        >
          {/* **Mark all read is always here, even at zero unread** — vanilla
              keeps it, and a control that appears only once there is something
              to press is a control nobody learns exists. It is also the only
              thing naming the panel, so the head cannot be conditional. */}
          <div className="notif-head">
            <span>Notifications</span>
            <button onClick={() => markRead.mutate()} disabled={markRead.isPending}>
              Mark all read
            </button>
          </div>

          <div id="notif-list" className="notif-list">
            {items.length === 0 ? (
              <p className="notif-empty m-0 px-2 py-6 text-center text-xs text-ink-faint">
                No notifications yet
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={String(n.id)}
                  onClick={() => {
                    setOpen(false);
                    // Straight to the task's own URL. If it has been archived or
                    // moved out of reach the modal answers "not here" for itself,
                    // which is a better answer than this guessing from a list it
                    // does not own.
                    navigate(`/board/task/${n.task_id}`);
                  }}
                  className={cn(
                    'notif-item flex w-full gap-2 rounded-lg px-2 py-2 text-left transition-colors duration-fast ease-out-soft hover:bg-bg',
                    !n.isRead && 'unread bg-action-bg/40',
                  )}
                >
                  <span className="mt-1 size-1.5 shrink-0 rounded-full" style={{ background: n.isRead ? 'transparent' : 'var(--color-action)' }} />
                  <span className="min-w-0 flex-1">
                    <span className="notif-title block text-xs leading-snug">
                      <strong>{n.actor_name ?? 'Somebody'}</strong> {VERB[n.type] ?? 'updated'}{' '}
                      <strong>{n.title ?? 'a task'}</strong>
                    </span>
                    {detailOf(n) && (
                      <span className="notif-detail mt-0.5 block truncate text-2xs text-ink-muted">{detailOf(n)}</span>
                    )}
                    <span className="notif-time mt-0.5 block text-2xs text-ink-faint">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * The one extra line worth showing, chosen by what happened.
 *
 * A note's excerpt is drawn with its formatting — the server sends the note as
 * typed when it is short enough to show whole — on a single line, with any list
 * flattened into bullets. Older excerpts and long ones are plain words and pass
 * through unchanged.
 */
function detailOf(n: Notification): React.ReactNode {
  if (n.type === 'moved' && n.from && n.to) return `${n.from} → ${n.to}`;
  if (n.type === 'message' && n.excerpt) return <InlineNote text={n.excerpt} />;
  return n.vertical ?? '';
}

function timeAgo(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
